import React, { useEffect, useState } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import { Button, Row, Col, Checkbox, Pagination } from "antd";
import styles from "./DashBoardMobile.module.scss";
import commonStyles from "../DashBoard.module.scss";
import moment from "moment";
import DashBoardMobileFilter from "./DashBoardMobileFilter";
import getTripIcon from "../../../helpers/getTripIcon";
import {
  backArrowIcon,
  circleRightArrow,
  dashBoardFilterIcon,
  greyCIcon,
  infoIcon,
} from "../../../assets/images";
import { ITripDetails } from "../../ServiceUsers/Interfaces/ITripDetails";
import { useTabPagination } from "../../../hooks/useTabPagination";
import FavoritesTabInfo from "../../Shared/FavoritesTabInfo";
import CustomTooltip from "../../../common/Tooltips/CustomTooltip";
import CustomButton from "../../../common/Buttons/CustomButton";
import { useTripsCtx } from "../../../context/TripDetailsContext";
import GoogleLoader from "../../../assets/Loader/GoogleSvgLoader/GoogleLoader";
import { usePaginationCtx } from "../../../context/PaginationContext";
import DashBoardMobileServiceDetails from "./DashBoardMobileServiceDetails";
import { defaultTripDetails } from "../../ServiceUsers/ServiceUsersMobile/defaultTripDetails";
import { SourcePageTypes } from "../../../types/TripTypes";

type Props = {
  serviceType: string;
  serviceUserId?: string;
  upcomingTripDetails: ITripDetails[];
  pastTripDetails: ITripDetails[];
  favoriteTripDetails: ITripDetails[];
  serviceUserName?: string;
  isCalling?: string;
};

const DashBoardMobileServices: React.FC<Props> = ({
  serviceType,
  serviceUserId,
  upcomingTripDetails,
  pastTripDetails,
  favoriteTripDetails,
  serviceUserName,
  isCalling,
}) => {
  const [title, setTitle] = useState<string>("");
  const [services, setServices] = useState<ITripDetails[]>([]);
  const [openFilter, setOpenFilter] = useState<boolean>(false);
  const [filterCount, setFilterCount] = useState<number>(1);
  const [selectedLeg, setSelectedLeg] = useState<ITripDetails>(
    defaultTripDetails
  );
  const [toggleDetailsView, setToggleDetailsView] = useState<boolean>(false);

  const { isLoading } = useTripsCtx();

  const { activeTabCtx, showCanceledCtx, resetPagination } = usePaginationCtx();

  const [activeTab, setActiveTab] = activeTabCtx;
  const [showCanceled, setShowCanceled] = showCanceledCtx;
  const [sourcePage, setSourcePage] = useState<string>(
    SourcePageTypes.Dashboard
  );

  const history = useHistory();
  const { pathname } = useLocation();

  const serviceTitles: { [service: string]: string } = {
    upcomingServices: "Upcoming Services",
    pastServices: "Past Services",
    favoriteServices: "Favorites",
  };

  useEffect(() => {
    setTitle(serviceTitles[serviceType]);
    setServices(
      {
        upcomingServices: upcomingTripDetails,
        pastServices: pastTripDetails,
        favoriteServices: favoriteTripDetails,
      }[serviceType] || []
    );
  }, [serviceType, upcomingTripDetails, pastTripDetails, favoriteTripDetails]);

  useEffect(() => {
    serviceType === "upcomingServices" && setActiveTab("1");
    serviceType === "pastServices" && setActiveTab("2");
    serviceType === "favoriteServices" && setActiveTab("3");

    const isDashboardCall = pathname
      .split("/")
      .some((pathSegment) => pathSegment === "ProfileMobilePage");
    setSourcePage(
      isDashboardCall ? SourcePageTypes.Dashboard : SourcePageTypes.SUProfile
    );
  }, []);

  const { paginationConfig } = useTabPagination(serviceUserId);

  const getTotalLegs = (requestAssignmentId: number) => {
    const totalLegs = favoriteTripDetails.filter(
      (trip: ITripDetails) => trip.requestAssignmentId === requestAssignmentId
    ).length;
    return totalLegs;
  };

  const showDrawer = (): void => setOpenFilter(true);
  const handleFilterCount = (count: number): void => setFilterCount(count);
  const resetToggleDetailsView = () => setToggleDetailsView(false);

  const handleBack = (): void => {
    activeTab !== "1" && setActiveTab("1");
    resetPagination();
    isCalling === "serviceUsersMobile"
      ? history.push(`/Dashboard/ManageServiceUsers/${serviceUserId}`)
      : history.push("/DashBoard");
  };
  const handleShowCanceled = () => setShowCanceled(!showCanceled);

  const handleToggleServiceDetails = (leg: ITripDetails) => {
    setToggleDetailsView(true);
    setSelectedLeg(leg);
  };

  return (
    <div className={styles.serviceContainer}>
      {toggleDetailsView ? (
        <DashBoardMobileServiceDetails
          selectedLeg={selectedLeg}
          resetToggleDetailsView={resetToggleDetailsView}
        />
      ) : (
        <>
          <Row>
            <Col span={24}>
              <CustomButton
                text="Back"
                handlerFunction={handleBack}
                type="link"
                icon={backArrowIcon}
                className="backBtn"
              />
            </Col>
          </Row>
          <Row className={styles.serviceTitleMargin}>
            <Col span={18}>
              {isCalling === "serviceUsersMobile" ? (
                <span className={styles.serviceUserNameText}>
                  {serviceUserName}
                </span>
              ) : (
                ""
              )}
              <p className={styles.title}>{title}</p>
            </Col>
            <Col span={6}>
              <Row justify="end">
                <Col className={styles.noRipple}>
                  <Button
                    className={
                      filterCount > 0 ? styles.filterBtnCount : styles.filterBtn
                    }
                    onClick={showDrawer}
                  >
                    <div className={styles.filterBtnContent}>
                      {filterCount > 0 ? <span>{filterCount}</span> : ""}
                      <img src={dashBoardFilterIcon} alt="filter" />
                    </div>
                  </Button>
                </Col>
              </Row>
            </Col>
          </Row>

          {serviceType === "pastServices" && (
            <Row justify="end">
              <Col>
                <Checkbox
                  className={commonStyles.cancelCheckbox}
                  onChange={handleShowCanceled}
                  checked={showCanceled}
                >
                  Show Canceled{" "}
                  <img src={greyCIcon} alt="canceled services icon" />
                </Checkbox>
              </Col>
            </Row>
          )}

          {serviceType === "favoriteServices" && <FavoritesTabInfo />}

          {["upcomingServices", "pastServices"].includes(serviceType) &&
            (isLoading ? (
              <GoogleLoader />
            ) : (
              <Row className={styles.serviceListMargin}>
                <Col span={24}>
                  {services?.map((service) => (
                    <div
                      onClick={() => handleToggleServiceDetails(service)}
                      key={service.bookingId}
                    >
                      <Row align="middle" className={styles.tripRows}>
                        <Col span={4}>
                          <span className={styles.tripImageLarge}>
                            {getTripIcon(service.tripReason)}
                          </span>
                        </Col>
                        <Col span={16}>
                          <Row className={styles.boldText}>
                            <Col span={24}>{service.serviceUser}</Col>
                            <Col span={24}>{service.pickUpAddress1}</Col>
                          </Row>
                          <Row className={styles.normalText}>
                            <Col span={24}>
                              <span>
                                {moment(service.tripDate).format("MM/DD/YY")} · 
                                {service.isWillCall
                                  ? "Call when ready"
                                  : moment(
                                      service.appointmentTime ||
                                        service.requestPickTime
                                    ).format("hh:mm A")}
                              </span>
                            </Col>
                            <Col span={24}>
                              <span>${service.tripCost} </span>
                              {service.statusDetail === "Cancelled" && (
                                <img
                                  src={greyCIcon}
                                  className={styles.cancelIcon}
                                  alt="canceled services icon"
                                />
                              )}
                            </Col>
                          </Row>
                        </Col>
                        <Col span={4}>
                          <Row justify="end">
                            <img src={circleRightArrow} alt="right arrow" />
                          </Row>
                        </Col>
                      </Row>
                    </div>
                  ))}
                </Col>
              </Row>
            ))}

          {serviceType === "favoriteServices" &&
            (isLoading ? (
              <GoogleLoader />
            ) : (
              <Row className={styles.serviceListMargin}>
                <Col span={24}>
                  {services?.map((service) => (
                    <Link
                      to={`/DashBoard/FavoriteDetails/${service.personId}/${service.requestAssignmentId}/${service.bookingId}/${service.favoriteId}/${service.tripDate}?source=${sourcePage}`}
                      key={service.bookingId}
                    >
                      <Row align="middle" className={styles.tripRows}>
                        <Col span={4}>
                          <span className={styles.tripImageLarge}>
                            {getTripIcon(service.tripReason)}
                          </span>
                        </Col>
                        <Col span={16}>
                          <Row className={styles.boldText}>
                            <Col span={24}>{service.favoriteName}</Col>
                            <Col span={24}>{service.serviceUser}</Col>
                          </Row>
                          <Row className={styles.normalText}>
                            <Col span={24}>
                              <Row gutter={[4, 0]} align="middle">
                                <Col>
                                  {service.isWillCall
                                    ? "Call when ready"
                                    : moment(
                                        service.appointmentTime ||
                                          service.requestPickTime
                                      ).format("hh:mm A")}
                                </Col>
                                <Col>
                                  <CustomTooltip
                                    tooltipText={`You'll be able to edit the details of
                                        this favorite after clicking the 
                                        "Rebook" button.`}
                                    className="customTooltip"
                                    icon={infoIcon}
                                  />
                                </Col>
                              </Row>
                            </Col>
                            <Col span={24}>
                              {getTotalLegs(service.requestAssignmentId)} Trips
                            </Col>
                          </Row>
                        </Col>
                        <Col span={4}>
                          <Row justify="end">
                            <img src={circleRightArrow} alt="right arrow" />
                          </Row>
                        </Col>
                      </Row>
                    </Link>
                  ))}
                </Col>
              </Row>
            ))}

          <Row justify="end" gutter={[0, 20]}>
            <Col span={24} />
            <Col id={styles.mobilePagination}>
              <Pagination
                current={paginationConfig.current}
                pageSize={paginationConfig.pageSize}
                onChange={paginationConfig.onChange}
                total={paginationConfig.total}
                showSizeChanger={true}
                onShowSizeChange={paginationConfig.onShowSizeChange}
              />
            </Col>
          </Row>
          <DashBoardMobileFilter
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
            setServices={setServices}
            serviceType={serviceType}
            onFilterCount={handleFilterCount}
            isCalling={isCalling}
            upcomingTripDetails={upcomingTripDetails}
            pastTripDetails={pastTripDetails}
          />
        </>
      )}
    </div>
  );
};
export default DashBoardMobileServices;
